import {
  SensorOrSubSensorInfo,
  SensorOrSubSensorInfoByType,
} from '@components/cockpit/panels/monitoring/monitoring.types';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { ColorLabelEnum, hexColor } from '@utils/map/map.constants';
import { isMonitoringSensorWithSubSensors, isSensorWithSubSensors } from '@utils/map/sensor.utils';
import { isValidCoordinates } from '@utils/validation/coordinates.utils';

import { SensorUniqueKeys, SubSensorInfo } from '@/types/map.types';
import {
  RADAR_SENSORS,
  RadarSensorConfiguration,
  SensorConfiguration,
  SensorConfigurationByType,
  SensorOrSubSensorConfiguration,
  SensorOrSubSensorConfigurationByType,
  SENSORS_WITH_SUBSENSOR,
  SensorWithSubSensorType,
  SUB_SENSORS,
  SubSensorConfiguration,
  SubSensorConfigurationWithParentType,
} from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  ActiveMonitoringSensorByType,
  InactiveMonitoringSensor,
  InactiveMonitoringSensorByType,
  MonitoringEquipment,
  MonitoringSensor,
  MonitoringSensorByType,
} from '@/types/sensor/monitoring.types';
import {
  EditableSensorOrSubSensorWithShorthand,
  EditableType,
  EXCLUDED_FROM_EDITION,
  SensorOrSubSensorWithShorthand,
  SensorTypeWithShorthand,
} from '@/types/sensor/sensorInstallation.types';
import {
  AutomatonCabinetStatus,
  AutomatonNodeStateEnum,
  AutomatonSection,
  AutomatonSectionStatus,
  AutomatonSegment,
  AutomatonSegmentStatus,
  AutomatonSegmentTypeEnum,
  AutomatonSensorStatus,
  ConnectorStatusEnum,
  EffectiveSensorStatusEnum,
  EquipmentConfiguration,
  ExtendedSensor,
  FlirSensorConfiguration,
  PerimeterCamera,
  RobinMaxRadar,
  SensorTypeEnum,
  SpecificStateEnum,
  SpexerRadar,
  SubSensorTypeEnum,
  TechnicalDefectEnum,
  XentaSensorConfiguration,
} from '@/types/sensor/sensors';
import {
  SensorOrSubSensorStatus,
  SensorOrSubSensorStatusByType,
  SensorStatus,
  SensorWithSubSensorStatus,
  SubSensorStatus,
  SubSensorStatusWithoutSection,
} from '@/types/sensor/status.types';

import { sensorColorLabelFromSensorStatus } from './configuration.constants';
import { isNeutral } from './neutral/neutral.utils';

export function isMonitoringSensorOfType<T extends SensorTypeEnum>(
  monitoringSensor: MonitoringSensor | undefined | null,
  type: T,
): monitoringSensor is MonitoringSensorByType<T> {
  if (!monitoringSensor) {
    return false;
  }
  return monitoringSensor.type === type;
}

export function isActiveMonitoringSensor(
  monitoringSensor: MonitoringSensor,
): monitoringSensor is ActiveMonitoringSensor {
  return monitoringSensor.status !== null;
}

export function isInactiveMonitoringSensor(
  monitoringSensor: MonitoringSensor,
): monitoringSensor is InactiveMonitoringSensor {
  return monitoringSensor.status === null;
}

export function isSensorOrSubSensorConfigurationOfType<T extends SensorTypeEnum | SubSensorTypeEnum>(
  configuration: SensorOrSubSensorConfiguration | undefined | null,
  type: T,
): configuration is SensorOrSubSensorConfigurationByType<T> {
  if (!configuration) {
    return false;
  }
  return configuration.type === type;
}

export function isSensorOrSubSensorStatusOfType<T extends SensorTypeEnum | SubSensorTypeEnum>(
  status: SensorOrSubSensorStatus | AutomatonSegmentStatus | undefined | null,
  type: T,
): status is SensorOrSubSensorStatusByType<T> {
  if (!status) {
    return false;
  }
  if ('type' in status) {
    return status.type === type;
  }
  return type === SubSensorTypeEnum.AUTOMATON_SEGMENT;
}

export function isAutomaton(
  monitoringSensor: MonitoringSensor,
): monitoringSensor is MonitoringSensorByType<SensorTypeEnum.AUTOMATON> {
  return isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.AUTOMATON);
}

export function isActiveAutomaton(
  monitoringSensor: ActiveMonitoringSensor,
): monitoringSensor is ActiveMonitoringSensorByType<SensorTypeEnum.AUTOMATON> {
  return isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.AUTOMATON);
}

export function isActiveGroundStation(
  monitoringSensor: ActiveMonitoringSensor,
): monitoringSensor is ActiveMonitoringSensorByType<SensorTypeEnum.GROUND_STATION> {
  return isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.GROUND_STATION);
}

export type AllSensorType =
  | SensorOrSubSensorConfiguration
  | PerimeterCamera
  | EquipmentConfiguration
  | SubSensorStatus
  | MonitoringSensor
  | SensorOrSubSensorStatus
  | MonitoringEquipment
  | null;

export function isMonitoringSensor(
  monitoringSensor: AllSensorType,
): monitoringSensor is MonitoringSensor | MonitoringEquipment {
  return monitoringSensor ? 'configuration' in monitoringSensor : false;
}

export function getSensorUniqueKey(sensor: AllSensorType): string {
  if (sensor === null) {
    return '';
  }
  return isMonitoringSensor(sensor) ? sensor.configuration.uniqueKey : sensor.uniqueKey;
}

export function getSensorStatusLabelKey({ active, maintenance, configuration, status }: SensorOrSubSensorInfo) {
  if (!active) {
    return 'sensors.inactive';
  } else if (maintenance) {
    return 'sensors.maintenance';
  } else if (!status) {
    return `sensors.status.${ConnectorStatusEnum.UNREACHABLE}`;
  } else if (configuration.type === SensorTypeEnum.AUTOMATON) {
    if (status.effectiveStatus === ConnectorStatusEnum.UNREACHABLE) {
      return `sensors.status.CONNECTION_DEFECT`;
    }
  }
  return `sensors.status.${status.effectiveStatus}`;
}

export function isRadarSensor(
  sensorConfiguration: SensorConfiguration,
): sensorConfiguration is RadarSensorConfiguration {
  return RADAR_SENSORS.some((type) => isSensorOrSubSensorConfigurationOfType(sensorConfiguration, type));
}

export function isSensorStatusWithSubSensorStatus(
  sensorStatus: SensorStatus,
): sensorStatus is SensorWithSubSensorStatus {
  return SENSORS_WITH_SUBSENSOR.some((type) => isSensorOrSubSensorStatusOfType(sensorStatus, type));
}

export function isSubSensorConfiguration(
  configuration: SensorOrSubSensorConfiguration,
): configuration is SubSensorConfiguration {
  return SUB_SENSORS.some((type) => isSensorOrSubSensorConfigurationOfType(configuration, type));
}

export function getSubSensorStatusesFromParent(sensorStatus: SensorStatus): SubSensorStatusWithoutSection[] {
  if (isSensorStatusWithSubSensorStatus(sensorStatus)) {
    return sensorStatus.subSensorStatuses;
  }
  return [];
}

export function getSubSensorConfigsFromParent<Config extends SensorConfigurationByType<SensorWithSubSensorType>>(
  parentConfiguration: Config,
): SubSensorConfigurationWithParentType<Config['type']>[] {
  switch (parentConfiguration.type) {
    case SensorTypeEnum.ARDRONIS:
      return parentConfiguration.antennas as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.AUTOMATON:
      return parentConfiguration.cabinets as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.GENERIC:
      return parentConfiguration.subSensors as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.ROBIN_MAX:
      return parentConfiguration.radars as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.SENID:
      return parentConfiguration.antennas as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.SKY_EYE:
      return parentConfiguration.antennas as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.SKY_JACKER:
      return parentConfiguration.antennas as SubSensorConfigurationWithParentType<Config['type']>[];
    case SensorTypeEnum.SPEXER:
      return parentConfiguration.radars as SubSensorConfigurationWithParentType<Config['type']>[];
    default:
      return [] as never;
  }
}

export function getRadarConfiguration(
  sensorConfiguration: RadarSensorConfiguration,
): RobinMaxRadar[] | SpexerRadar[] | FlirSensorConfiguration | XentaSensorConfiguration {
  if (isSensorOrSubSensorConfigurationOfType(sensorConfiguration, SensorTypeEnum.SPEXER)) {
    return getSubSensorConfigsFromParent(sensorConfiguration);
  } else if (isSensorOrSubSensorConfigurationOfType(sensorConfiguration, SensorTypeEnum.ROBIN_MAX)) {
    return getSubSensorConfigsFromParent(sensorConfiguration);
  } else {
    return sensorConfiguration;
  }
}

export function getSubsensorInfos<Sensor extends MonitoringSensorByType<SensorWithSubSensorType>>(
  parentMonitoringSensor: Sensor,
): SubSensorInfo<Sensor['configuration']['type']>[] {
  const parentSensorConfig = parentMonitoringSensor.configuration;
  return getSubSensorConfigsFromParent(parentSensorConfig).map((subSensorConfig) => {
    const subSensorStatus =
      parentMonitoringSensor.status?.subSensorStatuses.find((subStatus) => {
        return subStatus.uniqueKey === subSensorConfig.uniqueKey;
      }) ?? null;
    return {
      parentSensorConfig,
      subSensorConfig,
      subSensorStatus,
    } as SubSensorInfo<Sensor['configuration']['type']>;
  });
}

export function hasSubSensorsWithCoordinates(sensorConfiguration: SensorConfiguration) {
  if (
    !isSensorWithSubSensors(sensorConfiguration) ||
    isSensorOrSubSensorConfigurationOfType(sensorConfiguration, SensorTypeEnum.AUTOMATON)
  ) {
    return false;
  }
  return getSubSensorConfigsFromParent(sensorConfiguration).some((a) => isValidCoordinates(a.position));
}

export function isSensorConfiguration(sensor: SensorOrSubSensorConfiguration): sensor is SensorConfiguration {
  return !isSubSensorConfiguration(sensor);
}

export function isHostConfiguration(sensor: SensorConfiguration) {
  return 'host' in sensor;
}

export function isSubSensorUniqueKeys(uniqueKeys: SensorUniqueKeys) {
  return 'parentUniqueKey' in uniqueKeys;
}

export function isSectionRepulsionOn(sectionStatus: AutomatonSectionStatus) {
  return (
    !!sectionStatus.lowerSegment?.states[AutomatonNodeStateEnum.BT_DETECTION] ||
    !!sectionStatus.upperSegment?.states[AutomatonNodeStateEnum.BT_DETECTION]
  );
}

export function getCabinetStatuses(automatonStatus: AutomatonSensorStatus | null): AutomatonCabinetStatus[] {
  return automatonStatus === null ? [] : automatonStatus.subSensorStatuses;
}

export function getCabinetSectionStatuses(cabinetStatus: AutomatonCabinetStatus | null): AutomatonSectionStatus[] {
  if (!cabinetStatus) return [];
  return cabinetStatus.subSensorStatuses.filter(
    (sectionStatus) => sectionStatus.specificState !== SpecificStateEnum.SLEEP,
  );
}

export function getAutomatonSectionStatuses(automatonStatus: AutomatonSensorStatus | null) {
  const cabinetStatuses: AutomatonCabinetStatus[] = getCabinetStatuses(automatonStatus);
  return cabinetStatuses.flatMap((cabinetStatus) => getCabinetSectionStatuses(cabinetStatus));
}

export type DefenceState = 'detection' | 'repulsion' | 'none';

export function getDefenceState(sectionStatuses: AutomatonSectionStatus[]): DefenceState {
  if (!sectionStatuses.length) {
    return 'none';
  }
  if (sectionStatuses.every((sectionStatus) => !isSectionRepulsionOn(sectionStatus))) {
    return 'detection';
  }
  if (sectionStatuses.every((sectionStatus) => isSectionRepulsionOn(sectionStatus))) {
    return 'repulsion';
  }
  return 'none';
}

function checkAutomatonAllCabinetsSleepStatus(automatonStatus: AutomatonSensorStatus, isAsleep: boolean): boolean {
  const cabinetsStatuses = getCabinetStatuses(automatonStatus);

  return cabinetsStatuses.some(
    (cabinetsStatus) => isAsleep === (cabinetsStatus.specificState === SpecificStateEnum.SLEEP),
  );
}

export function automatonHasOneCabinetPoweredOff(automatonStatus: AutomatonSensorStatus): boolean {
  return checkAutomatonAllCabinetsSleepStatus(automatonStatus, true);
}

export function automatonHasOneCabinetPoweredOn(automatonStatus: AutomatonSensorStatus): boolean {
  return checkAutomatonAllCabinetsSleepStatus(automatonStatus, false);
}

export function toAutomatonSection(segments: AutomatonSegment[]) {
  const lowerSegment = segments.find((s) => s.segmentType === AutomatonSegmentTypeEnum.LOWER);
  const upperSegment = segments.find((s) => s.segmentType === AutomatonSegmentTypeEnum.UPPER);
  return { lowerSegment, upperSegment } as AutomatonSection;
}

export function isCircuitBreakerEnabled(status: AutomatonCabinetStatus) {
  return status.cabinetStates.TECHNICAL_DEFECT?.includes(TechnicalDefectEnum.CIRCUIT_BREAKER) ?? false;
}

export function isEmergencyStopEnabled(status: AutomatonCabinetStatus) {
  return status.cabinetStates.TECHNICAL_DEFECT?.includes(TechnicalDefectEnum.EMERGENCY_STOP) ?? false;
}

export function getSensorColorLabel(
  active: boolean,
  maintenance: boolean,
  type: ExtendedSensor,
  specificState: SpecificStateEnum | null | undefined,
  status?: EffectiveSensorStatusEnum | null,
): ColorLabelEnum {
  if (!active) {
    return ColorLabelEnum.WHITE;
  } else if (maintenance) {
    return ColorLabelEnum.ORANGE;
  } else if (status) {
    if (isNeutral(type) && specificState === SpecificStateEnum.EMITTING) {
      return ColorLabelEnum.RED;
    } else if (specificState === SpecificStateEnum.SLEEP) {
      return ColorLabelEnum.GRAY;
    }
    return sensorColorLabelFromSensorStatus[status];
  } else return ColorLabelEnum.PURPLE;
}

export function hasSpecificState(value: SensorOrSubSensorStatus | AutomatonSegmentStatus) {
  return 'specificState' in value;
}

export function getSensorHexColor(sensorOrSubSensorInfo: SensorOrSubSensorInfo): string {
  const specificState =
    sensorOrSubSensorInfo.status && hasSpecificState(sensorOrSubSensorInfo.status)
      ? sensorOrSubSensorInfo.status.specificState
      : undefined;
  return hexColor[
    getSensorColorLabel(
      sensorOrSubSensorInfo.active,
      sensorOrSubSensorInfo.maintenance,
      sensorOrSubSensorInfo.type,
      specificState,
      sensorOrSubSensorInfo.status?.effectiveStatus,
    )
  ];
}

function getSubSensorAndSensorConfigurationAndStatusOfAutomaton(
  monitoringSensor:
    | ActiveMonitoringSensorByType<SensorTypeEnum.AUTOMATON>
    | InactiveMonitoringSensorByType<SensorTypeEnum.AUTOMATON>,
): SensorOrSubSensorInfoByType<
  SensorTypeEnum.AUTOMATON | SubSensorTypeEnum.AUTOMATON_SEGMENT | SubSensorTypeEnum.AUTOMATON_CABINET
>[] {
  const { active, maintenance } = monitoringSensor.configuration;
  return [
    {
      ...monitoringSensor,
      maintenance,
    },
    ...getSubSensorConfigsFromParent(monitoringSensor.configuration).flatMap((subConfiguration) => {
      const cabinetStatus =
        monitoringSensor.status?.subSensorStatuses.find((status) => status.uniqueKey === subConfiguration.uniqueKey) ??
        null;
      return [
        {
          configuration: subConfiguration,
          status: cabinetStatus,
          type: subConfiguration.type,
          active: subConfiguration.active && active,
          maintenance: subConfiguration.maintenance || maintenance,
        },
        ...subConfiguration.segments.map((segmentConfiguration) => {
          const sectionStatus =
            cabinetStatus?.subSensorStatuses.find(
              (sectionStatus) =>
                sectionStatus.lowerSegment?.uniqueKey === segmentConfiguration.uniqueKey ||
                sectionStatus.upperSegment?.uniqueKey === segmentConfiguration.uniqueKey,
            ) ?? null;
          return {
            configuration: segmentConfiguration,
            status:
              segmentConfiguration.segmentType === AutomatonSegmentTypeEnum.LOWER
                ? (sectionStatus?.lowerSegment ?? null)
                : (sectionStatus?.upperSegment ?? null),
            type: segmentConfiguration.type,
            active: segmentConfiguration.active && subConfiguration.active && active,
            maintenance: segmentConfiguration.maintenance || subConfiguration.maintenance || maintenance,
          };
        }),
      ];
    }),
  ];
}

export function getSubSensorAndSensorConfigurationAndStatus(
  monitoringSensor: MonitoringSensor,
): SensorOrSubSensorInfo[] {
  const { active, maintenance } = monitoringSensor.configuration;
  const sensorInfo = {
    ...monitoringSensor,
    maintenance,
  };
  if (isMonitoringSensorWithSubSensors(monitoringSensor)) {
    if (isMonitoringSensorOfType(monitoringSensor, SensorTypeEnum.AUTOMATON)) {
      return [...getSubSensorAndSensorConfigurationAndStatusOfAutomaton(monitoringSensor)];
    } else {
      return [
        sensorInfo,
        ...getSubSensorConfigsFromParent(monitoringSensor.configuration).map((subConfiguration) => ({
          configuration: subConfiguration,
          type: subConfiguration.type,
          status:
            monitoringSensor.status?.subSensorStatuses.find(
              (status) => status.uniqueKey === subConfiguration.uniqueKey,
            ) ?? null,
          active: subConfiguration.active && active,
          maintenance: subConfiguration.maintenance || maintenance,
        })),
      ];
    }
  }
  return [sensorInfo];
}

export function isEditableSensor(
  sensor: SensorOrSubSensorWithShorthand,
): sensor is EditableSensorOrSubSensorWithShorthand {
  return EXCLUDED_FROM_EDITION.every((excludedType) => sensor.type !== excludedType);
}

export function isEditableType(type: SensorTypeWithShorthand): type is EditableType {
  return EXCLUDED_FROM_EDITION.every((excludedType) => type !== excludedType);
}

type AllKeys<T> = T extends SensorOrSubSensorConfiguration ? keyof T : never;

export function getDuplicatedConfiguration<T extends SensorOrSubSensorConfiguration>(sensor: T): T {
  const valuesToReset = {
    code: '',
    name: '',
    port: 0,
    host: '',
    remotePort: null,
    remoteHost: null,
    antennas:
      'antennas' in sensor && sensor.antennas.length >= 1 ? [getDuplicatedConfiguration(sensor.antennas[0])] : [],
    subSensors:
      'subSensors' in sensor && sensor.subSensors.length >= 1 ? [getDuplicatedConfiguration(sensor.subSensors[0])] : [],
    radars: 'radars' in sensor && sensor.radars.length >= 1 ? [getDuplicatedConfiguration(sensor.radars[0])] : [],
    cabinets:
      'cabinets' in sensor && sensor.cabinets.length >= 1 ? [getDuplicatedConfiguration(sensor.cabinets[0])] : [],
    segments:
      'segments' in sensor && sensor.segments.length >= 1 ? [getDuplicatedConfiguration(sensor.segments[0])] : [],
  } as Partial<SensorOrSubSensorConfiguration>;

  const valuesToRemove: AllKeys<SensorOrSubSensorConfiguration>[] = [
    'id',
    'sensorId',
    'uniqueKey',
    'createdBy',
    'modifiedBy',
    'modificationTime',
    'creationTime',
  ];
  const newSensor = { ...sensor };

  (Object.entries(valuesToReset) as [keyof T, unknown][]).forEach(([key, value]) => {
    if (key in sensor) {
      newSensor[key] = value as T[typeof key];
    }
  });

  valuesToRemove.forEach((key) => {
    if (key in newSensor) {
      delete newSensor[key as keyof T];
    }
  });

  return newSensor;
}

export type SensorToastParams = {
  command: string;
  name: string;
  commandValues?: { [p: string]: string | number | boolean };
};

export function createSensorToast({
  command,
  name,
  toast,
  commandValues,
}: SensorToastParams & { toast: ToastStatusEnum }) {
  createToast(
    appIntl().formatMessage(
      {
        id: `sensors.${toast.toLowerCase()}`,
      },
      {
        command: appIntl().formatMessage({ id: command }, { ...commandValues }),
        name,
      },
    ),
    toast,
  );
}
